import { HttpErrorResponse } from '@angular/common/http';
import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AccessService } from 'app/access/services/access.service';
import { UserRole } from 'app/core/authentication';
import { SessionStorageConstants } from 'app/core/kh-utils/session-storage-constants.enum';
import { SingleSignOnService } from 'app/shared/services/single-sign-on.service';
import { ClassModel } from 'app/teacher/models/class.model';
import { TeacherGoogleClassroomDataService } from 'app/teachers-shared/services/teacher-google-classroom-data.service';
import { TeacherGoogleClassroomService } from 'app/teachers-shared/services/teacher-google-classroom.service';
import { catchError } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'kh-oauth-google-handler',
    templateUrl: './google.component.html',
    styleUrls: ['./google.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class OauthGoogleHandlerComponent {
  public role: UserRole.student | UserRole.teacher | UserRole.parent | undefined;
  public constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly accessService: AccessService,
    private readonly singleSignOnService: SingleSignOnService,
    private readonly teacherGoogleClassroomService: TeacherGoogleClassroomService,
    private readonly teacherGoogleClassroomDataService: TeacherGoogleClassroomDataService) {}

  ngOnInit() {

    const isPendingProcess = this.teacherGoogleClassroomService.isPendingImport();
    if (!isPendingProcess) {
      // normal Oauth process
      this.accessService.loadInfo();
      this.role = this.accessService.role;
      this.singleSignOnService.checkAuth();
      return;
    }

    const token = this.route.snapshot.queryParamMap.get('code');
    if (!token) {
      // proceeding as normal Oauth process
      // in case we didnt cleanup last import correctly
      console.error('Missing token');
      this.accessService.loadInfo();
      this.role = this.accessService.role;
      this.singleSignOnService.checkAuth();
      return;
    }


    const process = this.teacherGoogleClassroomService.getImportProcess();

    if (process == 'GoogleClassroomImport') {    
      this.loadClassData(token);
    }
    else if (process == 'GoogleClassroomStudentImport') { 
      this.loadStudentData(token);
    }
  }


  private loadClassData(token:string): void {
    this.teacherGoogleClassroomDataService.loadClassData(token).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        return '';
      }),
      untilDestroyed(this)
    ).subscribe({
      next: courses => {
        sessionStorage.setItem(SessionStorageConstants.GoogleClassRoomCourses, JSON.stringify(courses));      
        const redirect = this.teacherGoogleClassroomService.getRedirect();      
        if (redirect) {       
          this.router.navigate(redirect.url, { queryParams: redirect.params });
        }
      }
    });
  }

  private loadStudentData(token:string): void {   
    const googleCourse = sessionStorage.getItem(SessionStorageConstants.GoogleClassRoomCourse);
    if (!googleCourse){    
      this.accessService.loadInfo();
      this.role = this.accessService.role;
      this.singleSignOnService.checkAuth();
      return;
    }
    const course: ClassModel = JSON.parse(googleCourse);

    this.teacherGoogleClassroomDataService.loadStudentData(course.ExternalId, token).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        return '';
      }),
      untilDestroyed(this)
    ).subscribe({
      next: students => {    
        sessionStorage.setItem(SessionStorageConstants.GoogleClassRoomCourseStudents, JSON.stringify(students));       
        const redirect = this.teacherGoogleClassroomService.getRedirect();     
        if (redirect) {        
          this.router.navigate(redirect.url, { queryParams: redirect.params });
        }
      }
    });
  }
}
